import Button from '@components/Button/Button';
import { CheckboxNoFunc } from '@components/Checkbox';
import { Collapse } from '@components/Collapse';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import { NumInput } from '@components/NumInput';
import Tip from '@components/Tip';
import IconClose from '@components/icons/IconClose';
import { Listbox } from '@headlessui/react';
import { $editingHotspotApi } from '@stores/editingHotspotStores';
import { $loadedScene } from '@stores/loadedStore';
import type {
  ComponentLayerControlItem,
  HSExtendActionItem,
  Hotspot,
} from '@types';
import { useStoreMap } from 'effector-react';
import type { Draft } from 'immer';
import { useMemo } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';

interface Props {
  hotspot: Hotspot;
}

const visibleItems: DropdownItem<ComponentLayerControlItem['visible']>[] = [
  { id: 0, name: '隐藏' },
  { id: 1, name: '显示' },
  { id: 2, name: '显示/隐藏交替' },
];

interface LayerDropdownItem {
  id: string;
  name: string;
}

export function ExtendLayerSettings({ hotspot }: Props) {
  const origLayerList = useStoreMap({
    store: $loadedScene,
    keys: [],
    fn: (scene) => scene!.config.components ?? [],
  });
  const controlList = hotspot.extendAction?.layers ?? [];

  /** 下拉菜单中可选的图层列表 */
  const layerList: LayerDropdownItem[] = useMemo(() => {
    const list: LayerDropdownItem[] = origLayerList.map((layer) => ({
      id: layer.id,
      name: layer.title,
    }));
    return list;
  }, [origLayerList]);

  /** 下拉菜单中选中的图层列表 */
  const selectedList: LayerDropdownItem[] = useMemo(() => {
    return layerList
      .filter((item) => controlList.find((control) => control.id === item.id))
      .sort((a, b) => {
        // 依照controlList的顺序排序, 解决乱续问题
        const indexA = controlList.findIndex((control) => control.id === a.id);
        const indexB = controlList.findIndex((control) => control.id === b.id);
        return indexA - indexB;
      });
  }, [controlList, layerList]);

  /** 图层设置中的列表 */
  const itemList: (HSExtendActionItem & {
    title: string;
  })[] = useMemo(() => {
    return controlList.map((item) => ({
      ...item,
      title: origLayerList.find((x) => x.id === item.id)?.title ?? '',
    }));
  }, [controlList, origLayerList]);

  const update = (updater: (draft: Draft<HSExtendActionItem[]>) => void) => {
    $editingHotspotApi.update((draft) => {
      draft.extendAction ??= {};
      draft.extendAction.layers ??= [];
      updater(draft.extendAction.layers);
    });
  };

  const renderItem = (item: HSExtendActionItem & { title: string }) => {
    return (
      <div key={item.id} className="mt-20">
        <div className="flex items-center justify-between">
          <div className="truncate" title={item.title}>
            {item.title}
          </div>
          <IconClose
            className="ml-20 shrink-0"
            onClick={() => {
              update((draft) => {
                const index = draft.findIndex((x) => x.id === item.id);
                if (index !== -1) {
                  draft.splice(index, 1);
                }
              });
            }}
          />
        </div>
        <div className="mt-10 flex items-center justify-between">
          <div className="w-135">
            <Dropdown
              selected={item.action}
              items={visibleItems}
              onChange={(val) => {
                update((draft) => {
                  const _item = draft.find((x) => x.id === item.id);
                  if (_item) {
                    _item.action = val;
                  }
                });
              }}
            />
          </div>
          <NumInput
            className="ml-10 w-55 px-10"
            min={0}
            max={9999}
            value={item.duration}
            digits={1}
            onChange={(val) => {
              update((draft) => {
                const _item = draft.find((x) => x.id === item.id);
                if (_item) {
                  _item.duration = val;
                }
              });
            }}
          />
          <div className="ml-5 text-black-gray">秒</div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Collapse title="热点控制图层" className="mt-10">
        <Listbox
          as="div"
          className="relative"
          value={selectedList}
          onChange={(vals) => {
            update((draft) => {
              const arr = vals.map((val) => {
                const originalItem = draft.find((item) => item.id === val.id);
                return {
                  id: val.id,
                  action: originalItem ? originalItem.action : 0,
                  duration: originalItem ? originalItem.duration : 0,
                };
              });
              draft.length = 0;
              draft.push(...arr);
            });
          }}
          multiple
        >
          <Listbox.Button as={Button} className="w-full">
            添加目标图层
          </Listbox.Button>
          <Listbox.Options className="absolute right-0 z-50 w-full pt-5 focus:outline-none">
            <div className="overflow-hidden rounded bg-white text-black focus:outline-none">
              <Scrollbars autoHeight autoHeightMax={375}>
                {layerList.map((x) => {
                  return (
                    <Listbox.Option
                      key={x.id}
                      className={({ active }) =>
                        `flex h-30 cursor-pointer items-center truncate px-10 ${
                          active && 'bg-white-light'
                        }`
                      }
                      value={x}
                    >
                      {({ selected }) => (
                        <>
                          <CheckboxNoFunc
                            checked={selected}
                            className="mr-5 shrink-0"
                          />
                          <div className="truncate" title={x.name}>
                            {x.name}
                          </div>
                        </>
                      )}
                    </Listbox.Option>
                  );
                })}
              </Scrollbars>
            </div>
          </Listbox.Options>
        </Listbox>
        <Tip className="mt-10">添加点击后需要隐藏或显示的图层</Tip>
        {itemList.map((item) => renderItem(item))}
      </Collapse>
    </>
  );
}
